import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { ILeagueMatchList } from '@/interface/ILeagueMatchList';
import { LanguageType, MarketPage, SportType } from '@/interface/enum';
import { useZone } from '@/libraries/composables/useZone';
import { getSportTypeKey } from '@/libraries/helpers/sportsHelper';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { invertCapitalize } from '@/libraries/string';
import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useDynamicMarketBetSlipStore } from '@/stores/betSlip/dynamicMarketBetSlipStore';
import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
import { useSingleBetSlipStore } from '@/stores/betSlip/singleBetSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

export function useLeague(matches: ILeagueMatchList[]) {
    const firstMatch = computed(() => matches[0]);

    const leagueName = computed(() => getTranslated(firstMatch.value.leagueName));
    const leagueInformation = computed(() => getTranslated(firstMatch.value.type === 'match' ? firstMatch.value.leagueInformation : firstMatch.value.leagueInfo));
    const { marketPage, isExpandAll } = storeToRefs(useRootStore());
    const isOutright = computed(() => firstMatch.value.type === 'outright' || marketPage.value === MarketPage.Outright);

    const { isSportsCupLeague: _isSportCupLeague } = useZone();
    const isSportsCupLeague = computed(() => {
        const tournamentName = getTranslated(firstMatch.value.leagueName, LanguageType.EN);
        return _isSportCupLeague(tournamentName);
    });

    const { isSportsCupIconEnabled } = storeToRefs(useFeatureToggleStore());
    const { isSupportMpTwoSubBets } = useFeatureToggleStore();
    const isTwoMpSubBetLeague = computed(() => isSupportMpTwoSubBets(firstMatch.value.type === 'match' ? firstMatch.value.leagueId : firstMatch.value.id));
    const { isMixParlayMode } = storeToRefs(useRootStore());

    const isExpanded = ref(true);

    const isShowTwoMpSubBetInformation = computed(() => isExpanded.value && isMixParlayMode.value && isTwoMpSubBetLeague.value);

    const { isShowBetBuilderBetSlip, isShowSingleBetSlip, isShowDynamicBetSlip, isShowParlayBetSlip } = storeToRefs(useBetSlipStore());
    const { betSlip } = storeToRefs(useSingleBetSlipStore());
    const { subBets } = storeToRefs(useParlayBetSlipStore());
    const { betSlip: betBuilderBetSlip } = storeToRefs(useBetBuilderBetSlipStore());
    const { betSlip: dynamicBetSlip } = storeToRefs(useDynamicMarketBetSlipStore());

    const hasOddsSelect = computed(() => {
        if (isShowBetBuilderBetSlip.value) {
            return betBuilderBetSlip.value.match.leagueId === (firstMatch.value.type === 'match' ? firstMatch.value.leagueId : firstMatch.value.id)
                && firstMatch.value.type === 'match'
                && matches.some(y => y.type === 'match' && betBuilderBetSlip.value.match.matchId === y.eventId)
                && betBuilderBetSlip.value.match.marketPage === firstMatch.value.marketPage;
        }

        if (isShowSingleBetSlip.value && !isMixParlayMode.value) {
            return betSlip.value.leagueId === (firstMatch.value.type === 'match' ? firstMatch.value.leagueId : firstMatch.value.id)
                && firstMatch.value.type === 'match'
                && matches.some(y => y.type === 'match' && betSlip.value.eventId === y.eventId)
                && betSlip.value.marketPageType === firstMatch.value.marketPage;
        }
        if (isShowDynamicBetSlip.value) {
            return dynamicBetSlip.value.match.leagueId === (firstMatch.value.type === 'match' ? firstMatch.value.leagueId : firstMatch.value.id)
                && matches.some(y => y.type === 'match' && dynamicBetSlip.value.match.matchId === y.eventId);
        }

        return isMixParlayMode.value
            && isShowParlayBetSlip.value
            && subBets.value.some(ticket => ticket.leagueId === (firstMatch.value.type === 'match' ? firstMatch.value.leagueId : firstMatch.value.id)
                && firstMatch.value.type === 'match'
                && matches.some(y => y.type === 'match' && ticket.eventId === y.eventId)
                && ticket.marketPageType === firstMatch.value.marketPage);
    });

    const isLive = computed(() => firstMatch.value.isLive);
    const sportType = computed(() => (firstMatch.value.type === 'match'
        ? firstMatch.value.sportType
        : SportType.Unknown));

    const iconName = computed(() => (sportType.value !== SportType.Unknown
        ? `sportIcon_${invertCapitalize(getSportTypeKey(sportType.value))}`
        : ''));

    function toggleExpanded() {
        isExpanded.value = !isExpanded.value;
    }

    watch(isExpandAll, () => {
        isExpanded.value = isExpandAll.value;
    });

    return {
        leagueName,
        leagueInformation,
        isOutright,
        isExpanded,
        isSportsCupLeague,
        isSportsCupIconEnabled,
        isTwoMpSubBetLeague,
        isMixParlayMode,
        hasOddsSelect,
        isShowTwoMpSubBetInformation,
        isLive,
        sportType,
        iconName,
        toggleExpanded,
    };
}
